import { useState, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import CircularProgress from "@mui/material/CircularProgress";
import { GOMAOWN_API_URL } from "../../config/serviceConfig";
import { DeviceGrid, PasswordCard } from "./YouLaberShared";

/**
 * CC 实验员选择对话框（ccma "综合训练"内输入 "YouLaber..." 触发的兜底入口）
 *
 * 与 YouLaberPanel 共用 DeviceGrid + PasswordCard，视觉一致；
 * 这里的 Dialog 仅做"全屏暗背景 + 居中容器"的外壳，去掉 MUI 默认的标题/按钮陈旧感。
 *
 * Props:
 *   open         boolean
 *   courseId     string   当前课程 ID（可空）
 *   deviceType   string   设备类型前缀（可空，拉取全部 cc- 设备）
 *   onSelect     ({device_id, device_name, device_type, device_password}) => void
 *   onClose      () => void
 *   apiUrl       string   后端 API 基础地址；CCMA 模式下传入 CCMA_API_URL（:8001）
 */
const CCDeviceSelector = ({ open, courseId, deviceType, onSelect, onClose, apiUrl }) => {
  const baseUrl = apiUrl || GOMAOWN_API_URL;
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingDevice, setPendingDevice] = useState(null);
  const [passwordError, setPasswordError] = useState("");

  const fetchDevices = useCallback(async () => {
    if (!open) return;
    try {
      const params = new URLSearchParams();
      if (courseId) params.append("course_id", courseId);
      const res = await fetch(`${baseUrl}/api/claw/cc-devices?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      let list = Array.isArray(data) ? data : (data.devices || []);
      if (deviceType) list = list.filter((d) => (d.device_type || "").startsWith(deviceType));
      setDevices(list);
      setError("");
    } catch (e) {
      setError("获取实验员列表失败：" + e.message);
    } finally {
      setLoading(false);
    }
  }, [open, courseId, deviceType, baseUrl]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchDevices();
    const timer = setInterval(fetchDevices, 5000);
    return () => clearInterval(timer);
  }, [open, fetchDevices]);

  const handleClose = () => {
    setPendingDevice(null);
    setPasswordError("");
    onClose();
  };

  const handleDeviceClick = (device) => {
    if (device.status === "offline") return;
    if (device.has_password) {
      setPendingDevice(device);
      setPasswordError("");
    } else {
      onSelect({
        device_id: device.device_id,
        device_name: device.device_name,
        device_type: device.device_type,
        device_password: "",
      });
      handleClose();
    }
  };

  const handlePasswordConfirm = (pwd) => {
    onSelect({
      device_id: pendingDevice.device_id,
      device_name: pendingDevice.device_name,
      device_type: pendingDevice.device_type,
      device_password: pwd,
    });
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: "transparent",
          boxShadow: "none",
          overflow: "visible",
        },
      }}
      BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,0.65)", backdropFilter: "blur(2px)" } }}
    >
      <div style={{
        background: "linear-gradient(180deg, #161b22 0%, #0d1117 100%)",
        border: "1px solid rgba(88,166,255,0.2)",
        borderRadius: "14px",
        padding: "24px 26px 22px",
        color: "#e6edf3",
        boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
      }}>
        {pendingDevice ? (
          <PasswordCard
            device={pendingDevice}
            error={passwordError}
            onCancel={() => setPendingDevice(null)}
            onConfirm={handlePasswordConfirm}
          />
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "baseline", gap: "10px", marginBottom: "4px" }}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>选择 YouLabber 智能实验员</h3>
              <span style={{ color: "#6e7681", fontSize: "11px" }}>每 5 秒自动刷新</span>
            </div>
            <p style={{ margin: "4px 0 18px", color: "#8b949e", fontSize: "12px" }}>
              选择一位空闲的实验员开始本次实验；忙碌的实验员可排队等待。
            </p>

            {loading && devices.length === 0 ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
                <CircularProgress size={28} />
              </div>
            ) : (
              <DeviceGrid devices={devices} onSelect={handleDeviceClick} />
            )}

            {error && (
              <div style={{ color: "#f85149", padding: "10px 0 0", fontSize: "12px" }}>{error}</div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
              <button
                onClick={handleClose}
                style={{
                  background: "transparent", border: "1px solid rgba(255,255,255,0.15)",
                  color: "#9aa4b2", padding: "6px 16px", borderRadius: "6px",
                  fontSize: "12px", cursor: "pointer", fontFamily: "inherit",
                }}
              >取消</button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default CCDeviceSelector;
